import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import MemoCardPreview from "./MemoCardPreview";
import {
  trackMemoCardPageView,
  trackMemoCardStarted,
} from "./MemoCardAnalytics";

const Start = () => {
  const theme = useTheme();

  useEffect(() => {
    trackMemoCardPageView("start");
  }, []);

  // Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12 } }),
  };

  const wordVariants = {
    enter: { y: 20, opacity: 0 },
    center: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
  };

  const handleStart = () => {
    trackMemoCardStarted();
    window.location.assign("/memo-card/questions");
  };

  return (
    <>
      <Helmet>
        <title>Memo-Cards | Flying Comet Games</title>
        <meta
          name="description"
          content="Make someone cry (in a good way). A memory card built just for Mother's Day."
        />
        <meta property="og:title" content="Memo-Cards | Flying Comet Games" />
        <meta
          property="og:description"
          content="Make someone cry (in a good way) with a custom memory card built for Mother's Day."
        />
        <meta
          property="og:image"
          content="https://flyingcometgames.com/assets/memo-cards/memo-card-preview.png"
        />
        <meta
          property="og:url"
          content="https://flyingcometgames.com/memo-card"
        />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Memo-Cards | Flying Comet Games" />
        <meta
          name="twitter:description"
          content="Make someone cry (in a good way) with a custom memory card built for Mother's Day."
        />
        <meta
          name="twitter:image"
          content="https://flyingcometgames.com/assets/memo-cards/memo-card-preview.png"
        />
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          p: 3,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Animated floating SVG background */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 0.13, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          style={{
            position: "absolute",
            top: -60,
            left: -80,
            width: 320,
            height: 320,
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg width="320" height="320" viewBox="0 0 400 400">
            <defs>
              <radialGradient id="bg-grad-memo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#f472b6" />
              </radialGradient>
            </defs>
            <circle cx="200" cy="200" r="180" fill="url(#bg-grad-memo)" />
          </svg>
        </motion.div>

        {/* Mother's Day flower background */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.03 }}
          transition={{ duration: 1.5 }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "200px",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          🌸
        </motion.div>

        <Box
          sx={{
            maxWidth: "md",
            mx: "auto",
            position: "relative",
            zIndex: 1,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}
          className="memo-header-section"
        >
          {/* Mother's Day Badge */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <Typography
                className="md-badge"
                sx={{
                  display: "inline-block",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  bgcolor: "rgba(168,85,247,0.08)",
                  color: "primary.main",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 600,
                  border: "1px solid rgba(168,85,247,0.15)",
                }}
              >
                🌷 Just for Mother's Day (May 12)
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: "center",
                mb: 0.5,
                fontSize: { xs: "1.7rem", sm: "2.2rem" },
                fontWeight: 700,
              }}
              className="memo-title"
            >
              Memo-Cards
            </Typography>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 2,
                color: "text.secondary",
                fontSize: { xs: "1.08rem", sm: "1.15rem" },
                fontWeight: 500,
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
              className="memo-emotional-subtitle"
            >
              A memory card you make for
              <br /> a mom you love.
            </Typography>
          </motion.div>
          {/* MemoCardPreview component */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={4}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MemoCardPreview />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <motion.button
              type="button"
              onClick={handleStart}
              whileHover={{
                scale: 1.05,
                background: "linear-gradient(90deg, #b366f8, #f583c0)",
                boxShadow: "0px 0px 16px rgba(168,85,247,0.2)",
              }}
              whileTap={{
                scale: 0.98,
                background: "linear-gradient(90deg, #9d44f6, #f361a9)",
              }}
              style={{
                width: "100%",
                padding: "14px 0",
                fontSize: "1.13rem",
                fontWeight: 700,
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(90deg, #a855f7, #f472b6)",
                color: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                marginBottom: 0,
                boxShadow: "0 2px 12px 0 rgba(168,85,247,0.10)",
                transition: "all 0.2s ease-in-out",
              }}
              className="memo-cta-btn"
            >
              <motion.span
                role="img"
                aria-label="sparkle"
                initial={{ rotate: 0 }}
                whileHover={{ rotate: 20, scale: 1.2 }}
                whileTap={{ rotate: -10, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ fontSize: "1.2em", marginRight: 6 }}
              >
                ✨
              </motion.span>
              Start Creating
            </motion.button>
          </motion.div>
        </Box>

        {/* Lightweight footer */}
        <Box
          sx={{
            textAlign: "center",
            opacity: 0.7,
            fontSize: "0.98rem",
            mt: "auto",
            py: 1,
          }}
          className="memo-footer"
        >
          <span>
            Built with{" "}
            <span role="img" aria-label="heart">
              💌
            </span>{" "}
            by{" "}
            <a
              href="https://flyingcometgames.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "none",
                borderBottom: "1px solid rgba(168,85,247,0.2)",
                transition: "border-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = "rgba(168,85,247,0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = "rgba(168,85,247,0.2)";
              }}
            >
              Flying Comet Games
            </a>
          </span>
        </Box>
      </Box>
    </>
  );
};

export default Start;
